<template>
  <div>
    <h1 class="page-title">Inscricoes</h1>

    <SemEventoBanner v-if="organizadorSemEventos" :nome="authStore.adminName" />

    <div v-if="!organizadorSemEventos" class="filters card">
      <input class="input search" type="text" v-model="q" @input="debouncedFetch" placeholder="Buscar por nome, CPF ou email..." />
      <select class="select" v-model="eventoSlug" @change="fetch()">
        <option value="">Todos eventos</option>
        <option v-for="e in eventosStore.eventos" :key="e.id" :value="e.slug">{{ e.nome }}</option>
      </select>
      <select class="select" v-model="status" @change="fetch()">
        <option value="">Todos status</option>
        <option value="PENDENTE">Pendente</option>
        <option value="PARCIAL">Parcial</option>
        <option value="QUITADA">Quitada</option>
        <option value="ATRASADA">Atrasada</option>
      </select>
    </div>

    <SkeletonLoader v-if="store.loading" variant="row" :count="8" />

    <div v-else-if="store.items.length" class="table-card card">
      <div class="table-wrap"><table class="table">
        <thead>
          <tr>
            <th>Participante</th>
            <th>Evento</th>
            <th>Plano</th>
            <th>Status</th>
            <th>Data</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="i in store.items" :key="i.id" @click="openDetail(i)" class="clickable">
            <td>
              <strong>{{ i.participanteNome }}</strong>
              <span v-if="i.grupoInscricao && i.grupoInscricao.tamanho > 1" class="grupo-badge" :title="`Grupo de ${i.grupoInscricao.tamanho}`">
                👥 {{ i.posicaoNoGrupo }}/{{ i.grupoInscricao.tamanho }}
              </span>
              <br>
              <small>{{ i.comprador?.email }}</small>
            </td>
            <td>{{ i.evento?.nome }}</td>
            <td>
              <span v-if="i.plano">{{ i.plano.nome }}</span>
              <span v-else class="text-muted">—</span>
            </td>
            <td><StatusChip :status="i.statusPagamento" /></td>
            <td>{{ formatDate(i.createdAt) }}</td>
            <td>→</td>
          </tr>
        </tbody>
      </table></div>

      <div v-if="store.meta.pages > 1" class="pagination">
        <button class="btn-secondary" :disabled="store.meta.page <= 1" @click="changePage(store.meta.page - 1)">‹ Anterior</button>
        <span class="page-info">Pagina {{ store.meta.page }} de {{ store.meta.pages }}</span>
        <button class="btn-secondary" :disabled="store.meta.page >= store.meta.pages" @click="changePage(store.meta.page + 1)">Proxima ›</button>
      </div>
    </div>

    <EmptyState
      v-else
      icon="📋"
      title="Sem inscricoes"
      description="Ainda nao ha inscritos nesse filtro."
    />

    <!-- Detail modal -->
    <div v-if="detail" class="modal-overlay" @click.self="detail = null">
      <div class="modal-card detail-modal">
        <h2 class="modal-title">{{ detail.participanteNome }}</h2>
        <div class="detail-grid">
          <div class="detail-row"><span>Evento</span><strong>{{ detail.evento?.nome }}</strong></div>
          <div class="detail-row"><span>CPF</span><strong>{{ formatCpf(detail.participanteCpf) }}</strong></div>
          <div class="detail-row"><span>Email</span><strong>{{ detail.comprador?.email }}</strong></div>
          <div class="detail-row"><span>WhatsApp</span><strong>{{ detail.comprador?.telefone }}</strong></div>
          <div class="detail-row"><span>Plano</span><strong>{{ detail.plano?.nome }}</strong></div>
          <div class="detail-row"><span>Status</span><StatusChip :status="detail.statusPagamento" /></div>
        </div>

        <div v-if="detail.grupoInscricao && detail.grupoInscricao.tamanho > 1" class="grupo-section">
          <h3 class="sub-title">
            👥 Grupo ({{ detail.grupoInscricao.tamanho }} pessoas)
            <small class="grupo-hint">1 cobrança para todo o grupo</small>
          </h3>
          <div class="grupo-list">
            <div
              v-for="p in detail.grupoInscricao.inscricoes"
              :key="p.id"
              class="grupo-item"
              :class="{ 'grupo-item-self': p.id === detail.id }"
            >
              <div class="grupo-pos">{{ p.posicaoNoGrupo }}</div>
              <div class="grupo-nome">
                <strong>{{ p.participanteNome }}</strong>
                <small v-if="p.participanteCpf">{{ formatCpf(p.participanteCpf) }}</small>
                <small v-else class="text-muted">sem CPF (convidada)</small>
              </div>
              <StatusChip :status="p.statusPagamento" />
              <span v-if="p.id === detail.id" class="tag-self">esta</span>
            </div>
          </div>
          <div v-if="authStore.canSeeFinanceiro" class="grupo-totals">
            <div class="detail-row"><span>Cobrado (total)</span><strong>{{ formatMoney(detail.grupoInscricao.valorCobrado) }}</strong></div>
            <div class="detail-row"><span>Inscrições ({{ detail.grupoInscricao.tamanho }} × {{ formatMoney(detail.grupoInscricao.valorPlanoTotal / detail.grupoInscricao.tamanho) }})</span><strong>{{ formatMoney(detail.grupoInscricao.valorPlanoTotal) }}</strong></div>
            <div class="detail-row"><span>Taxa de serviço</span><strong>{{ formatMoney((detail.grupoInscricao.comissaoPlataforma || 0) + (detail.grupoInscricao.taxaGateway || 0)) }}</strong></div>
          </div>
        </div>

        <div v-if="detail.cupomCodigo" class="cupom-applied">
          🎟️ Cupom <strong>{{ detail.cupomCodigo }}</strong>
          aplicou desconto de <strong>{{ formatMoney(detail.descontoTotal || 0) }}</strong>
          <span v-if="authStore.canSeeFinanceiroCompleto">
            ({{ formatMoney(detail.descontoPlataforma || 0) }} seu + {{ formatMoney(detail.descontoEvento || 0) }} do evento)
          </span>
        </div>

        <div v-if="detail.participanteCamposCustom && Object.keys(detail.participanteCamposCustom).length" class="custom-section">
          <h3 class="sub-title">Campos custom</h3>
          <div class="detail-grid">
            <div v-for="(v, k) in detail.participanteCamposCustom" :key="k" class="detail-row">
              <span>{{ k }}</span><strong>{{ v }}</strong>
            </div>
          </div>
        </div>

        <div v-if="authStore.canSeeFinanceiro && detail.pagamentos?.length" class="custom-section">
          <h3 class="sub-title">Pagamentos</h3>
          <div class="table-wrap"><table class="pag-table">
            <thead><tr><th>Vencimento</th><th>Bruto</th><th>Liquido</th><th>Status</th><th>Pago em</th></tr></thead>
            <tbody>
              <tr v-for="p in detail.pagamentos" :key="p.id">
                <td>{{ formatDate(p.vencimento) }}</td>
                <td>{{ formatMoney(p.valor) }}</td>
                <td class="liq">{{ p.valorLiquido != null ? formatMoney(p.valorLiquido) : '—' }}</td>
                <td><span class="mini-chip" :class="`mc-${p.status.toLowerCase()}`">{{ p.status }}</span></td>
                <td>{{ p.pagoEm ? formatDate(p.pagoEm) : '—' }}</td>
              </tr>
            </tbody>
          </table></div>
        </div>

        <div v-if="authStore.canManageEventos" class="custom-section">
          <h3 class="sub-title">Anotacoes (interno)</h3>
          <textarea class="textarea" rows="3" v-model="anotacaoLocal" @blur="salvarAnotacao" maxlength="2000"></textarea>
        </div>

        <div class="modal-actions">
          <button
            v-if="podeCancelar"
            class="btn-danger"
            :disabled="cancelando"
            @click="cancelarInscricao"
          >{{ cancelando ? 'Cancelando…' : 'Cancelar inscrição' }}</button>
          <button v-if="authStore.canManageEventos" class="btn-secondary" @click="syncInscricao">Sincronizar com Asaas</button>
          <button class="btn-primary" @click="detail = null">Fechar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import { useInscricoesStore } from '../../stores/inscricoes'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import StatusChip from '../../components/ui/StatusChip.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import SemEventoBanner from '../../components/ui/SemEventoBanner.vue'

export default {
  name: 'InscricoesPage',
  components: { SkeletonLoader, StatusChip, EmptyState, SemEventoBanner },
  data() {
    return {
      q: '', eventoSlug: '', status: '',
      detail: null,
      anotacaoLocal: '',
      debounceTimer: null,
      cancelando: false
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    store() { return useInscricoesStore() },
    organizadorSemEventos() {
      return this.authStore.isOrganizador && !this.eventosStore.loading && this.eventosStore.eventos.length === 0
    },
    podeCancelar() {
      if (!this.detail) return false
      const role = this.authStore.role
      const permitido = role === 'ROOT' || role === 'ADMIN' || role === 'ORGANIZADOR'
      if (!permitido) return false
      const s = this.detail.statusPagamento
      return s !== 'CANCELADA' && s !== 'QUITADA'
    }
  },
  async mounted() {
    await this.eventosStore.fetchAll()
    await this.fetch()
  },
  methods: {
    debouncedFetch() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.fetch(1), 350)
    },
    async fetch(page = 1) {
      await this.store.fetch({
        page,
        ...(this.eventoSlug && { eventoSlug: this.eventoSlug }),
        ...(this.status && { status: this.status }),
        ...(this.q && { q: this.q })
      })
    },
    changePage(p) { this.fetch(p) },
    async openDetail(inscricao) {
      try {
        const full = await this.store.getOne(inscricao.id)
        this.detail = full
        this.anotacaoLocal = full.anotacao || ''
      } catch {
        this.$toast.open({ message: 'Falha ao carregar', type: 'error', position: 'top-right' })
      }
    },
    async salvarAnotacao() {
      if (!this.detail || this.anotacaoLocal === (this.detail.anotacao || '')) return
      try {
        await this.store.updateAnotacao(this.detail.id, this.anotacaoLocal)
        this.detail.anotacao = this.anotacaoLocal
        this.$toast.open({ message: 'Anotacao salva', type: 'success', position: 'top-right', duration: 2000 })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    async syncInscricao() {
      if (!this.detail) return
      try {
        const res = await this.store.sync(this.detail.id)
        this.detail.statusPagamento = res.statusPagamento
        this.$toast.open({ message: 'Sincronizado com Asaas', type: 'success', position: 'top-right' })
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    async cancelarInscricao() {
      if (!this.detail) return
      const nome = this.detail.participanteNome
      const grupo = this.detail.grupoInscricao
      const emGrupo = grupo && grupo.tamanho > 1
      const msg = emGrupo
        ? `Essa inscrição faz parte de um GRUPO de ${grupo.tamanho} pessoas com uma única cobrança.\n\nCancelar aqui vai cancelar TODAS as ${grupo.tamanho} inscrições do grupo (${grupo.inscricoes.map(p => p.participanteNome).join(', ')}) e o pagamento no Asaas.\n\nContinuar?`
        : `Cancelar a inscrição de "${nome}"?\n\nIsso cancela a cobrança no Asaas e marca a inscrição como CANCELADA. Ação irreversível.`
      if (!confirm(msg)) return
      this.cancelando = true
      try {
        const res = await this.store.cancelar(this.detail.id)
        this.detail.statusPagamento = 'CANCELADA'
        this.$toast.open({ message: res?.message || 'Inscrição cancelada', type: 'success', position: 'top-right' })
        if (res?.avisos?.asaas) {
          this.$toast.open({ message: res.avisos.asaas, type: 'warning', position: 'top-right', duration: 8000 })
        }
        await this.fetch(this.store.meta?.page || 1)
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Falha ao cancelar', type: 'error', position: 'top-right' })
      } finally {
        this.cancelando = false
      }
    },
    formatDate(d) {
      try { return new Date(d).toLocaleDateString('pt-BR') } catch { return d }
    },
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    formatCpf(cpf) {
      const c = String(cpf || '').replace(/\D/g, '')
      if (c.length !== 11) return cpf
      return `${c.slice(0,3)}.${c.slice(3,6)}.${c.slice(6,9)}-${c.slice(9)}`
    }
  }
}
</script>

<style scoped>
.filters {
  display: flex; gap: var(--spacing-sm);
  padding: var(--spacing-base);
  margin-bottom: var(--spacing-base);
  flex-wrap: wrap;
}
.search { flex: 1 1 200px; min-width: 0; }
.select { flex: 1 1 140px; max-width: 200px; min-width: 0; }
@media (max-width: 540px) {
  .filters { padding: 12px; }
  .search, .select { max-width: 100%; flex-basis: 100%; }
}
.table-card { padding: 0; overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 10px var(--spacing-base);
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 12px var(--spacing-base);
  font-size: var(--text-sm);
  border-bottom: 1px solid var(--color-bg-separator);
}
.table tr.clickable { cursor: pointer; }
.table tr:hover td { background: var(--color-bg-hover); }
small { color: var(--color-text-muted); font-size: var(--text-xs); }
.text-muted { color: var(--color-text-muted); }
.pagination {
  display: flex; align-items: center; justify-content: center; gap: var(--spacing-base);
  padding: var(--spacing-base);
  border-top: 1px solid var(--color-bg-separator);
}
.page-info { font-size: var(--text-sm); color: var(--color-text-muted); }

.detail-modal { max-width: 640px; }
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-sm) var(--spacing-md); }
.detail-row { display: flex; flex-direction: column; gap: 2px; font-size: var(--text-sm); }
.detail-row span { color: var(--color-text-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
.custom-section { margin-top: var(--spacing-lg); }
.sub-title { font-size: var(--text-md); margin-bottom: var(--spacing-sm); }
.pag-table { width: 100%; font-size: var(--text-xs); }
.liq { color: var(--color-success); font-weight: 600; }
.cupom-applied {
  margin-top: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: #fef3c7; color: #92400e;
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
}
.pag-table th, .pag-table td { padding: 6px 4px; text-align: left; border-bottom: 1px solid var(--color-bg-separator); }
.mini-chip {
  display: inline-block;
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  padding: 1px 4px; border-radius: 3px;
}
.mc-pending { background: #fef3c7; color: #92400e; }
.mc-received { background: #dcfce7; color: #166534; }
.mc-overdue { background: #fee2e2; color: #b91c1c; }
.mc-refunded { background: #f3f4f6; color: #4b5563; }
.mc-deleted { background: #f3f4f6; color: #4b5563; }
.grupo-badge {
  display: inline-block; margin-left: 6px;
  background: rgba(37,99,235,0.1); color: var(--color-accent);
  font-size: 11px; font-weight: 700;
  padding: 2px 6px; border-radius: 10px;
  vertical-align: middle;
}
.grupo-section { margin-top: var(--spacing-lg); }
.grupo-section .sub-title { display: flex; align-items: baseline; gap: var(--spacing-sm); flex-wrap: wrap; }
.grupo-hint { font-size: var(--text-xs); color: var(--color-text-muted); font-weight: 400; text-transform: none; letter-spacing: 0; }
.grupo-list { display: flex; flex-direction: column; gap: var(--spacing-xs); }
.grupo-item {
  display: flex; align-items: center; gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
}
.grupo-item-self { background: var(--color-bg-hover); border-color: var(--color-accent); }
.grupo-pos {
  width: 28px; height: 28px; border-radius: 999px;
  background: var(--color-bg-hover); color: var(--color-text-secondary);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}
.grupo-nome { flex: 1; display: flex; flex-direction: column; }
.grupo-nome small { font-size: 11px; color: var(--color-text-muted); }
.tag-self {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  padding: 2px 6px; border-radius: 3px;
  background: var(--color-accent); color: #fff;
}
.grupo-totals {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-sm);
  border-top: 1px dashed var(--color-bg-separator);
  display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-xs) var(--spacing-md);
}
@media (max-width: 540px) {
  .grupo-totals { grid-template-columns: 1fr; }
}
@media (max-width: 540px) {
  .detail-grid { grid-template-columns: 1fr; }
}
</style>
